import { colorsFoundation } from "./colors-foundation.js";
import { activeBrand } from "../brands/index.js";
import { hexToRgba } from "../utils/colors.js";

export const colorsSemantic = {
  text: {
    inverse: colorsFoundation.neutral.white,
    default: colorsFoundation.neutral.softblack,
    disabled: colorsFoundation.neutral[300],
    subtle: colorsFoundation.neutral[700],
    // Cool-grey chrome text roles from the dummy (see colorsFoundation.gray).
    muted: colorsFoundation.gray[500],
    mutedStrong: colorsFoundation.gray[700],
    strong: colorsFoundation.gray[900],
    accent: {
      gold: colorsFoundation.key.gold[600],
      blue: colorsFoundation.key.blue[700],
    },
  },
  background: {
    default: colorsFoundation.neutral.white,
    paper: colorsFoundation.neutral.white,
    // Dark sidebar surface, skinned per brand (see theme/brands).
    sidebar: activeBrand.sidebar?.background ?? colorsFoundation.neutral[50],
    // Light-grey app/content background behind white cards (matches the dummy).
    page: colorsFoundation.gray[50],
    elevated: colorsFoundation.neutral.white,
    subtle: colorsFoundation.neutral[50],
  },
  border: {
    default: colorsFoundation.neutral[300],
    subtle: colorsFoundation.neutral[200],
    strong: colorsFoundation.neutral[400],
    disabled: colorsFoundation.neutral[200],
    // Cool-grey chrome borders from the dummy (cards, hairline dividers, controls).
    card: colorsFoundation.gray[200],
    hairline: colorsFoundation.gray[100],
    control: colorsFoundation.gray[300],
    // Legacy nested structure for backward compatibility
    neutral: {
      subtle: colorsFoundation.neutral[300],
      default: colorsFoundation.neutral[400],
      strong: colorsFoundation.neutral[700],
      disabled: colorsFoundation.neutral[200],
    },
    accent: {
      gold: {
        subtle: colorsFoundation.key.gold[300],
        default: colorsFoundation.key.gold[400],
        strong: colorsFoundation.key.gold[600],
      },
    },
  },
  surface: {
    neutral: {
      base: colorsFoundation.neutral.white,
      subtle: colorsFoundation.neutral[100],
    },
    // Cool-grey chrome surfaces from the dummy (table header tint, hovers).
    table: {
      headerBg: colorsFoundation.gray[100],
    },
    hover: colorsFoundation.gray[100],
    rowHover: colorsFoundation.gray[50],
    accent: {
      gold: {
        subtlest: colorsFoundation.key.gold[50],
        subtle: colorsFoundation.key.gold[100],
        default: colorsFoundation.key.gold[600],
        strong: colorsFoundation.key.gold[800],
      },
      blue: {
        default: colorsFoundation.key.blue[700],
      },
    },
  },
  icon: {
    default: colorsFoundation.neutral.softblack,
    inverse: colorsFoundation.neutral.white,
    subtle: colorsFoundation.neutral[700],
    disabled: colorsFoundation.neutral[300],
    // Cool-grey chrome icon role from the dummy.
    muted: colorsFoundation.gray[500],
    accent: {
      gold: colorsFoundation.key.gold[600],
      blue: colorsFoundation.key.blue[700],
    },
  },
  label: {
    schaden: {
      gespeichert: colorsFoundation.label.grey,
      entwurf: colorsFoundation.label.grey,
      pruefung: colorsFoundation.label.cyan,
      korrektur: colorsFoundation.label.orange,
      bearbeitung: colorsFoundation.label.yellow,
      abgeschlossen: colorsFoundation.label.blue,
      abgelehnt: colorsFoundation.label.red,
      teilreguliert: colorsFoundation.label.green,
    },
    buchung: {
      exportiert: colorsFoundation.label.purple,
      offen: colorsFoundation.label.orange,
      abgeschlossen: colorsFoundation.label.blue,
      freigegeben: colorsFoundation.label.green,
    },
    payment_import: {
      pending: colorsFoundation.label.grey,
      imported: colorsFoundation.label.yellow,
      assigned: colorsFoundation.label.green,
      failed: colorsFoundation.label.red,
    },
  },

  /**
   * Dashboard "Abwicklungs-Transparenz" roles: who the ball is with, how stale a
   * claim is, and the trend-bar ramp.
   *
   * The blues come off the brand ramp on purpose — on the schmidt skin
   * blue[700]/blue[900] already resolve to the dummy's #4A78BC/#1B2C45, so this
   * matches the design exactly while any other skin recolours for free. The
   * violet/amber/red are fixed semantic hues (responsibility and urgency), not
   * brand colours, so they stay literal.
   */
  attention: {
    // Zuständigkeit — must line up with the WaitingOn enum values.
    waiting: {
      makler: colorsFoundation.key.blue[700],
      versicherer: "#8b5cf6",
      kunde: "#E9A23B",
    },
    // Trend mini-chart bars.
    bar: {
      current: colorsFoundation.key.blue[700],
      recessive: hexToRgba(colorsFoundation.key.blue[700], 0.28),
      hover: colorsFoundation.key.blue[900],
      baseline: colorsFoundation.gray[200],
    },
    // Alterspyramide — keyed by the backend's age_bucket keys.
    age: {
      "0-7": colorsFoundation.key.blue[700],
      "8-14": hexToRgba(colorsFoundation.key.blue[700], 0.65),
      "15-30": "#E9A23B",
      "30+": "#E63F25",
    },
    overdue: "#E63F25",
    // Bar tracks behind the progress/age bars.
    track: "#f1f3f6",
    trackStrong: "#f4f6f9",
    // Month-over-month delta pill.
    delta: {
      good: { bg: "#E7F4EC", fg: "#15803d" },
      bad: { bg: "#FCE9E4", fg: "#E63F25" },
      neutral: { bg: colorsFoundation.gray[100], fg: colorsFoundation.gray[500] },
    },
    // Row flag chips.
    flag: {
      neu: { bg: "#FCE9E4", fg: "#E63F25" },
      overdue: { bg: "#FEF3C7", fg: "#B45309" },
    },
    // Filter pill in its "something needs attention" state.
    warn: { bg: "#FCE9E4", fg: "#B5341F", border: "#F1C6BC" },
    /**
     * Categorical ramp for the Sparte donut. Fixed order (hues assigned in
     * sequence, never cycled); the last slot is the "Sonstige" catch-all.
     * CVD-validated — the labelled legend carries identity for the closer hues.
     */
    categorical: ["#3b82f6", "#f59e0b", "#0ea5e9", "#8b5cf6", "#10b981", "#64748b"],
  },
};

export default colorsSemantic;
